<template>
  <div>
    <el-form
      ref="Form"
      :model="form"
      :rules="formRules"
      label-position="top"
      :disabled="isLoading"
      @submit.native.prevent="submitForm"
    >
      <div class="tw-flex md:tw-gap-10 tw-flex-wrap lg:tw-flex-nowrap tw-mb-10">
        <div class="tw-w-full lg:tw-w-1/3">
          <div class="tw-flex tw-flex-col tw-gap-3 tw-mb-7">
            <h3 class="tw-mb-1">
              {{ $t('foreigners.basic_info.sections.personal') }}
            </h3>

            <el-form-item
              :label="$t('common.fields.first_name')"
              :error="errors?.first_name?.[0]"
              :class="form.first_name ? 'has-value' : ''"
              prop="first_name"
            >
              <el-input
                v-model="form.first_name"
                :disabled="!$isInRole(['ADMIN', 'CONSULTANT'])"
              />
            </el-form-item>


            <el-form-item
              :label="$t('common.fields.last_name')"
              :error="errors?.last_name?.[0]"
              :class="form.last_name ? 'has-value' : ''"
              prop="last_name"
            >
              <el-input
                v-model="form.last_name"
                :disabled="!$isInRole(['ADMIN', 'CONSULTANT'])"
              />
            </el-form-item>

            <el-form-item
              v-if="$isInRole(['ADMIN', 'CONSULTANT'])"
              :label="$t('common.fields.gender')"
              :error="errors?.gender?.[0]"
              :class="form.gender ? 'has-value' : ''"
              prop="gender"
            >
              <el-select
                v-model="form.gender"
                filterable
                placeholder=""
              >
                <el-option
                  v-for="item in genders"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>

            <el-form-item
              :label="$t('common.fields.citizenship')"
              :error="errors?.citizenship_id?.[0]"
              :class="form.citizenship_id ? 'has-value' : ''"
              prop="profile.citizenship_id"
            >
              <el-select
                v-model="form.profile.citizenship_id"
                filterable
                placeholder=""
                :disabled="!$isInRole(['ADMIN', 'CONSULTANT'])"
              >
                <el-option
                  v-for="item in countries"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>

            <el-form-item>
              <el-switch
                v-model="form.profile.master"
                :disabled="!$isInRole(['ADMIN'])"
                :active-text="$t('foreigners.basic_info.family.is_head')"
                class="tw-mt-2"
              />
            </el-form-item>
          </div>

          <div class="tw-flex tw-flex-col tw-gap-4">
            <h3 class="tw-mb-4">
              {{ $t('foreigners.basic_info.sections.contact') }}
            </h3>

            <div class="tw-flex tw-flex-col tw-gap-3">
              <el-form-item
                :label="$t('common.fields.phone')"
                :error="errors?.phone_primary?.[0]"
                :class="form.phone_primary ? 'has-value' : ''"
                prop="phone_primary"
              >
                <el-input
                  v-model="form.phone_primary"
                  type="tel"
                  :disabled="!$isInRole(['ADMIN', 'CONSULTANT'])"
                />
              </el-form-item>

              <el-form-item
                :label="$t('common.fields.email')"
                :error="errors?.email?.[0]"
                :class="form.email ? 'has-value' : ''"
                prop="email"
              >
                <el-input
                  v-model="form.email"
                  type="email"
                  :disabled="!$isInRole(['ADMIN', 'CONSULTANT'])"
                />
              </el-form-item>
            </div>
          </div>
        </div>

        <div class="tw-w-full lg:tw-w-1/3">
          <div class="tw-flex tw-flex-col tw-gap-4 tw-mb-7">
            <h3>
              {{ $t('foreigners.basic_info.sections.internal') }}
            </h3>

            <div class="tw-flex tw-flex-col tw-gap-3">
              <el-form-item
                :label="$t('foreigners.filters.assigned_to')"
                :error="errors?.assigned_to_id?.[0]"
                :class="form.assigned_to_id ? 'has-value' : ''"
                prop="profile.assigned_to_id"
              >
                <el-select
                  v-model="form.profile.assigned_to_id"
                  placeholder=""
                  :disabled="!$isInRole(['ADMIN'])"
                  filterable
                  :filter-method="internalUsersFilterMethod"
                  @visible-change="handleInternalUsersVisibleChange"
                >
                  <el-option
                    v-for="item in filteredInternalUsers"
                    :key="item.id"
                    :label="item.fullname"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>

              <el-form-item
                :label="$t('main.me.interface_language')"
                :error="errors?.interface_language?.[0]"
                :class="form.interface_language ? 'has-value' : ''"
                prop="interface_language"
              >
                <el-select
                  v-model="form.interface_language"
                  placeholder=""
                  :disabled="me.role !== $roles.ADMIN"
                  filterable
                >
                  <el-option
                    v-for="item in languages"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>

              <el-form-item>
                <el-switch
                  v-model="form.is_active"
                  :disabled="!$isInRole(['ADMIN'])"
                  :active-text="$t('common.fields.is_active')"
                  class="tw-mt-2"
                />
              </el-form-item>
            </div>
          </div>

          <div class="tw-flex tw-flex-col tw-gap-4">
            <h3>
              {{ $t('foreigners.basic_info.sections.client') }}
            </h3>

            <div class="tw-flex tw-flex-col tw-gap-3">
              <el-form-item
                :label="$t('common.fields.company')"
                :error="errors?.profile?.client_company_id?.[0]"
                :class="form.profile?.client_company_id ? 'has-value' : ''"
                prop="profile.client_company_id"
              >
                <el-select
                  v-model="form.profile.client_company_id"
                  placeholder=""
                  :disabled="me.role === $roles.HR"
                  filterable
                  :filter-method="clientCompaniesFilterMethod"
                  @visible-change="handleClientCompaniesVisibleChange"
                >
                  <el-option
                    v-for="item in filteredClientCompanies"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>

              <el-form-item
                :label="$t('foreigners.basic_info.fields.hire_origin')"
                :error="errors?.profile?.hire_origin?.[0]"
                :class="form.profile?.hire_origin ? 'has-value' : ''"
                prop="profile.hire_origin"
              >
                <el-select
                  v-model="form.profile.hire_origin"
                  placeholder=""
                  :disabled="!$isInRole(['ADMIN', 'CONSULTANT', 'HR'])"
                  filterable
                >
                  <el-option
                    v-for="item in hireOrigins"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>

              <el-form-item
                :label="$t('foreigners.basic_info.assigned_hr')"
                :error="errors?.profile?.assigned_to_hr_id?.[0]"
                :class="form.profile?.assigned_to_hr_id ? 'has-value' : ''"
                prop="profile.assigned_to_hr_id"
              >
                <el-select
                  v-model="form.profile.assigned_to_hr_id"
                  placeholder=""
                  :disabled="me.role === $roles.HR"
                  filterable
                >
                  <el-option
                    v-for="item in clientUsersFilteredOptions"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>

              <el-form-item
                :label="$t('foreigners.basic_info.fields.cli_ref_number')"
                :error="errors?.profile?.client_reference_number?.[0]"
                :class="form.profile?.client_reference_number ? 'has-value' : ''"
                prop="profile.client_reference_number"
              >
                <el-input
                  v-model="form.profile.client_reference_number"
                  :disabled="!$isInRole(['ADMIN', 'CONSULTANT', 'HR'])"
                />
              </el-form-item>

              <el-form-item
                :label="$t('foreigners.basic_info.fields.off_job_code')"
                :error="errors?.profile?.official_job_code?.[0]"
                :class="form.profile?.official_job_code ? 'has-value' : ''"
                prop="profile.official_job_code"
              >
                <el-input
                  v-model="form.profile.official_job_code"
                  :disabled="!$isInRole(['ADMIN', 'CONSULTANT', 'HR'])"
                />
              </el-form-item>

              <el-form-item
                :label="$t('foreigners.basic_info.fields.supervisor')"
                :error="errors?.profile?.supervisor?.[0]"
                :class="form.profile?.supervisor ? 'has-value' : ''"
                prop="profile.supervisor"
              >
                <el-input
                  v-model="form.profile.supervisor"
                  :disabled="!$isInRole(['ADMIN', 'CONSULTANT', 'HR'])"
                />
              </el-form-item>
            </div>
          </div>
        </div>

        <div class="tw-w-full lg:tw-w-1/3">
          <div class="tw-mb-7">
            <div class="tw-mb-3.5 tw-flex tw-justify-between">
              <h3>
                {{ $t('foreigners.basic_info.sections.family_bound') }}
              </h3>

              <el-popover
                trigger="click"
                placement="bottom-end"
                :width="333"
                popper-class="is-pure"
              >
                <template #reference>
                  <i-button
                    type="transparent"
                    size="small"
                    class="tw-px-0"
                    :disabled="!$isInRole(['ADMIN', 'CONSULTANT'])"
                  >
                    <icon-plus />
                    <span class="tw-font-normal">
                      {{ $t('foreigners.basic_info.sections.family_bound') }}
                    </span>
                  </i-button>
                </template>

                <div class="tw-divide-y tw-divide-gray dark:tw-divide-dm-gray">
                  <div>
                    <button
                      class="tw-text-base tw-text-dark dark:tw-text-dm-dark tw-flex tw-items-center tw-p-6 hover:tw-text-primary dark:hover:tw-text-dm-primary"
                      @click="isAddFamilyMemberDialogOpen = true; isAddFamilyMemberDialogCreateNew = true"
                    >
                      <icon-user-add class="tw-mr-2" />
                      <span>
                        {{ $t('foreigners.basic_info.family_dialog.new_member') }}
                      </span>
                    </button>
                  </div>
                  <div>
                    <button
                      class="tw-text-base tw-text-dark dark:tw-text-dm-dark tw-flex tw-items-center tw-p-6 hover:tw-text-primary dark:hover:tw-text-dm-primary"
                      @click="isAddFamilyMemberDialogOpen = true; isAddFamilyMemberDialogCreateNew = false"
                    >
                      <icon-user-list class="tw-mr-2" />
                      <span>
                        {{ $t('foreigners.basic_info.family_dialog.new_member_existing') }}
                      </span>
                    </button>
                  </div>
                </div>
              </el-popover>

              <AddFamilyMemberDialog
                :is-open="isAddFamilyMemberDialogOpen"
                :is-create-new="!!isAddFamilyMemberDialogCreateNew"
                @close="isAddFamilyMemberDialogOpen = false"
                @refresh="reloadForeignerDetail"
              />
            </div>

            <div>
              <div
                v-for="person in familyEditable"
                :key="person.relation_with_id"
                class="tw-flex tw-mb-2"
              >
                <i-user-avatar
                  :fullname="person.relation_with_fullname"
                  size="medium"
                />
                <div class="tw-flex tw-flex-col tw-gap-3 tw-mx-3 tw-flex-1">
                  <el-form-item
                    :label="$t('common.fields.full_name')"
                  >
                    <el-input
                      v-model="person.relation_with_fullname"
                      disabled
                    />
                  </el-form-item>
                  <el-form-item
                    :label="$t('common.fields.relation_type')"
                  >
                    <el-select
                      v-model="person.relation_type"
                      placeholder=""
                      :disabled="!$isInRole(['ADMIN', 'CONSULTANT'])"
                      filterable
                      @change="(value) => changeFamilyMemberType(person, value)"
                    >
                      <el-option
                        v-for="item in familyRelations"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                      />
                    </el-select>
                  </el-form-item>
                </div>
                <i-button
                  type="primary"
                  size="large"
                  is-icon
                  @click="removeFamilyPerson(person.relation_with_id)"
                >
                  <icon-trash />
                </i-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="tw-border-t tw-border-gray dark:tw-border-dm-gray tw-flex tw-justify-end tw-gap-2 tw-py-6">
        <i-button
          class="tw-w-36"
          @click="$emit('close-edit')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          native-type="submit"
          class="tw-w-36"
          :is-loading="isLoading"
        >
          {{ $t('common.actions.save') }}
        </i-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import _cloneDeep from 'lodash/cloneDeep';
import _pick from 'lodash/pick';
import _get from 'lodash/get';
import _defaultsDeep from 'lodash/defaultsDeep';

import { deepDiff, getValidationError } from '@/helpers';

import AddFamilyMemberDialog from '@/components/Dialogs/AddFamilyMemberDialog.vue';
import { clientCompanyFilterMixin } from '@/mixins/clientCompanyFilterMixin';
import { internalUsersFilterMixin } from '@/mixins/internalUsersFilterMixin';

const BASE_FORM = {
  first_name: '',
  last_name: '',
  gender: '',
  email: '',
  phone_primary: '',
  is_active: false,
  interface_language: '',
  profile: {
    citizenship_id: '',
    client_company_id: '',
    assigned_to_id: '',
    assigned_to_hr_id: '',
    client_reference_number: '',
    official_job_code: '',
    master: false,
    supervisor: '',
    hire_origin: '',
  },
};

export default {
  name: 'V2ForeignersBasicInfoEdit',
  components: {
    AddFamilyMemberDialog,
  },
  mixins: [internalUsersFilterMixin, clientCompanyFilterMixin],
  data() {
    return {
      errors: {},
      form: _cloneDeep(BASE_FORM),
      familyEditable: [],
      formRules: {
        first_name: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        last_name: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        gender: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        'profile.citizenship_id': [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        email: [
          { type: 'email', message: 'Please input correct email address', trigger: 'blur' },
        ],
      },
      isLoading: false,
      isAddFamilyMemberDialogOpen: false,
      isAddFamilyMemberDialogCreateNew: false,
    };
  },

  computed: {
    ...mapState({
      me: (state) => state.v2.shared.profile.me,
      countries: (state) => state.v2.shared.enums.enums.countries,
      genders: (state) => state.v2.shared.enums.enums.genders,
      familyRelations: (state) => state.v2.shared.enums.enums.family_relations,
      languages: (state) => state.v2.shared.enums.enums.languages,
      hireOrigins: (state) => state.v2.shared.enums.enums.hire_origins,
      foreigner: (state) => state.v2.admin.foreigners.foreigner.foreigner,
    }),
    ...mapGetters({
      internalUsers: 'v2/admin/cms/internal/users/selectOptions',
      clientUsers: 'v2/admin/cms/clients/users/selectOptions',
      clientCompanies: 'v2/admin/cms/clients/companies/selectOptions',
    }),

    foreignerFiltered() {
      return _defaultsDeep(_pick(this.foreigner, [
        'first_name',
        'last_name',
        'gender',
        'email',
        'phone_primary',
        'is_active',
        'interface_language',

        'profile.citizenship_id',
        'profile.client_company_id',
        'profile.assigned_to_id',
        'profile.assigned_to_hr_id',
        'profile.client_reference_number',
        'profile.official_job_code',
        'profile.master',
        'profile.supervisor',
        'profile.hire_origin',
      ]), _cloneDeep(BASE_FORM));
    },
    clientUsersFilteredOptions() {
      const options = this.clientUsers.filter((el) => el.client_company_id === this.form.profile.client_company_id && el.is_active);
      return [{ fullname: ' ', id: null },...options];
    },
  },

  mounted() {
    this.$store.dispatch('v2/admin/cms/clients/companies/loadAllCompanies');
    this.$store.dispatch('v2/admin/cms/internal/users/loadAllUsers');
    this.$store.dispatch('v2/admin/cms/clients/users/loadAllUsers');

    this.form = _cloneDeep(this.foreignerFiltered);
    this.familyEditable = _cloneDeep(this.foreigner.family);
  },

  beforeUnmount() {
    this.form = _cloneDeep(BASE_FORM);
    this.familyEditable = [];
  },

  methods: {
    async submitForm() {
      this.errors = {};
      let switchToViewModeAfter = true;
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;
      const data = deepDiff(this.foreignerFiltered, this.form);

      try {
        await this.$store.dispatch('v2/admin/foreigners/foreigner/editForeignerDetail', {
          id: this.foreigner.id,
          payload: data,
        });
      } catch (e) {
        switchToViewModeAfter = false;
        this.errors = e.response.data.data;

        this.$message.error({
          message: this.$capitalize(e.response.data.message),
          showClose: true,
        });
      }

      // Edit family members
      const familyChangesPromises = [];
      this.familyEditable.forEach((member) => {
        const original = this.foreigner.family.find(({ relation_with_id }) => relation_with_id === member.relation_with_id);
        const diff = deepDiff(original, member);

        if (Object.keys(diff).length) {
          const request = this.$store.dispatch('v2/admin/foreigners/foreigner/editForeignerFamily', {
            id: this.foreigner.id,
            memberId: member.id,
            payload: diff,
          }).catch((error) => {
            switchToViewModeAfter = false;
            const message = _get(error, 'response.data.data.relation_with_id');
            this.$message({
              showClose: true,
              message: this.$capitalize(message || 'Error'),
              type: 'error',
            });
          });
          familyChangesPromises.push(request);
        }
      });
      await Promise.all(familyChangesPromises);

      // Delete family members
      const familyMemberToDeletePromises = [];
      this.foreigner.family.forEach((member) => {
        const isDeleted = !this.familyEditable.find(({ relation_with_id }) => relation_with_id === member.relation_with_id);
        if (isDeleted) {
          const request = this.$store.dispatch('v2/admin/foreigners/foreigner/deleteForeignerFamilyMember', {
            id: this.foreigner.id,
            memberId: member.id,
          }).catch((error) => {
            switchToViewModeAfter = false;
            const message = _get(error, 'response.data.data');
            this.$message({
              showClose: true,
              message: this.$capitalize(message || 'Error'),
              type: 'error',
            });
          });
          familyMemberToDeletePromises.push(request);
        }
      });
      await Promise.all(familyMemberToDeletePromises);

      this.reloadForeignerDetail();

      this.isLoading = false;

      switchToViewModeAfter && this.$emit('close-edit');
    },

    async reloadForeignerDetail() {
      await this.$store.dispatch('v2/admin/foreigners/foreigner/loadForeignerDetail', this.foreigner.id);

      this.form = _cloneDeep(this.foreignerFiltered);
      this.familyEditable = _cloneDeep(this.foreigner.family);
    },

    removeFamilyPerson(relation_with_id) {
      this.familyEditable = this.familyEditable.filter((person) => person.relation_with_id !== relation_with_id);
    },

    changeFamilyMemberType({ relation_with_id }, relation_type) {
      this.familyEditable = this.familyEditable.map((person) => {
        if (person.person === relation_with_id) {
          person.relation_type = relation_type;
        }
        return person;
      });
    },
  },
};
</script>
